import { For, Index, Show, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import {
  STORY_SETS,
  type LegendRow,
  type StoryCard,
  type StorySet,
  backStep,
  ctaLabel,
  nextStep,
  segmentFilled,
} from "../stories";

/**
 * Figma S1–S6 / Z1–Z3 story player (section 114:13958), opened as a full-screen violet overlay over
 * the ride sheet. Moves only on "Suivant" / "Retour" (no auto-advance, no tap zones, no swipe).
 * X or Escape closes it (`onClose`); the last card's CTA calls `onDone` (else `onClose`).
 * The animation is an animated WebP, replaced by its static PNG when the rider prefers reduced
 * motion (or the browser has no WebP).
 */
export function Stories(props: { set: StorySet; onClose: () => void; onDone?: () => void }) {
  let dialog: HTMLDivElement | undefined;
  const def = () => STORY_SETS[props.set];
  const [index, setIndex] = createSignal(0);
  const card = () => def().cards[index()];
  const titleId = `stories-title-${Math.random().toString(36).slice(2, 8)}`;

  const next = () => {
    const step = nextStep(index(), def().cards.length);
    if (step.done) (props.onDone ?? props.onClose)();
    else setIndex(step.index);
  };
  const back = () => {
    setIndex(backStep(index()));
    // "Retour" is gone on the first card: keep the focus inside the dialog.
    if (index() === 0) dialog?.focus();
  };

  onMount(() => {
    dialog?.focus();
    // On the document, not the dialog: a removed focused button would drop the focus to <body>.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    document.addEventListener("keydown", onKey);
    onCleanup(() => document.removeEventListener("keydown", onKey));
  });

  // Warm the next card's animation so "Suivant" doesn't show an empty box.
  createEffect(() => {
    const nextArt = def().cards[index() + 1]?.art;
    if (nextArt && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
      new Image().src = nextArt.webp;
    }
  });

  return (
    <div
      ref={(el) => (dialog = el)}
      class="fixed inset-0 z-50 flex flex-col bg-violet text-white outline-none"
      style={{
        "padding-top": "max(12px, env(safe-area-inset-top))",
        "padding-bottom": "max(16px, env(safe-area-inset-bottom))",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabindex="-1"
    >
      {/* Progress: 3 × 4 px bars, 6 px apart, 20 px side margins; done and current at 100 %. */}
      <div class="flex shrink-0 gap-[6px] px-5" aria-hidden="true">
        <Index each={def().cards}>
          {(_, i) => (
            <span
              class={`h-1 flex-1 rounded-[2px] bg-white ${segmentFilled(i, index()) ? "" : "opacity-30"}`}
            />
          )}
        </Index>
      </div>
      <span class="sr-only" aria-live="polite">
        {`${def().category}, étape ${index() + 1} sur ${def().cards.length}`}
      </span>

      {/* Header row: 50 px (Figma hotspots y 62–112). */}
      <div class="relative h-[50px] shrink-0">
        <Show when={index() > 0}>
          <button
            type="button"
            class="absolute left-0 top-1 flex h-11 w-[120px] items-center pl-[14px] text-[15px] font-medium leading-[1.25] text-white"
            onClick={back}
          >
            <img src="/img/stories/arrow-back-white-22.svg" width="22" height="22" alt="" />
            <span class="ml-1">Retour</span>
          </button>
        </Show>
        <button
          type="button"
          class="absolute right-0 top-0 flex h-[50px] w-[58px] items-start pl-4 pt-3"
          onClick={() => props.onClose()}
          aria-label="Fermer"
        >
          <img src="/icons/close-white-26.svg" width="26" height="26" alt="" />
        </button>
      </div>

      <Show when={card()} keyed>
        {(c) => (
          <>
            <div class="flex shrink-0 flex-col items-center px-[30px] pt-[10px] text-center short:pt-0">
              <span class="inline-flex h-7 items-center rounded-[14px] bg-white/18 px-4 text-[13px] font-medium leading-[1.25]">
                {def().category}
              </span>
              <h2
                id={titleId}
                class="mt-[18px] w-full max-w-[342px] text-[30px] font-semibold leading-[1.12] short:mt-3 short:text-[26px]"
              >
                {c.title}
              </h2>
              <Show when={c.body}>
                <p class="mt-[14px] w-full max-w-[330px] text-base leading-[1.35] text-white/90 short:text-[15px]">
                  {c.body}
                </p>
              </Show>
            </div>
            <Show when={c.legend} fallback={<Illustration card={c} />}>
              {(rows) => <Legend rows={rows()} />}
            </Show>
          </>
        )}
      </Show>

      <div class="shrink-0 px-[15px]">
        <button
          type="button"
          class="flex h-[50px] w-full items-center justify-center rounded-[4px] bg-white text-base font-medium uppercase leading-[1.25] tracking-[0.32px] text-violet shadow-[0_1.5px_1.5px_rgba(0,0,0,0.18)] active:bg-surface2"
          onClick={next}
        >
          {ctaLabel(props.set, index())}
        </button>
      </div>
    </div>
  );
}

/** The card's animation, centred in the room left between the text and the CTA. */
function Illustration(props: { card: StoryCard }) {
  return (
    <div class="relative my-6 min-h-0 flex-1 short:my-3">
      <Show when={props.card.art}>
        {(a) => (
          <picture>
            {/* Animated only when motion is welcome; the PNG otherwise, or without WebP. */}
            <source
              type="image/webp"
              media="(prefers-reduced-motion: no-preference)"
              srcset={a().webp}
            />
            <img
              src={a().png}
              width={a().width}
              height={a().height}
              alt=""
              class="absolute inset-0 m-auto max-h-full max-w-full object-contain"
              style={{ width: `${a().width}px`, height: `${a().height}px` }}
            />
          </picture>
        )}
      </Show>
    </div>
  );
}

/** Z2: 40 px white discs at x 24, title Medium 15 and text Regular 13 at 85 % from x 80. */
function Legend(props: { rows: LegendRow[] }) {
  return (
    <ul class="mt-[18px] mb-4 min-h-0 flex-1 overflow-y-auto pl-6 pr-[24px]">
      <For each={props.rows}>
        {(row) => (
          <li class="mb-3 flex items-start gap-4">
            <LegendIcon icon={row.icon} />
            <span class="min-w-0 flex-1">
              <span class="block text-[15px] font-medium leading-[1.25]">{row.title}</span>
              <span class="mt-px block text-[13px] leading-[1.23] text-white/85">{row.text}</span>
            </span>
          </li>
        )}
      </For>
    </ul>
  );
}

function LegendIcon(props: { icon: LegendRow["icon"] }) {
  if (props.icon === "zone-limit") {
    // The dashed white ring is drawn without a disc.
    return (
      <img
        src="/img/stories/legend-zone-limit-40.png"
        width="40"
        height="40"
        alt=""
        class="shrink-0"
      />
    );
  }
  const src = {
    parking: "/img/stories/legend-parking-28.svg",
    speed: "/img/stories/legend-speed-orange-22.svg",
    warning: "/icons/warning-red.svg",
    "no-parking": "/img/stories/legend-parking-red-22.svg",
  }[props.icon];
  const size = props.icon === "parking" ? 28 : 22;
  return (
    <span class="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white">
      <img src={src} width={size} height={size} alt="" />
      <Show when={props.icon === "no-parking"}>
        {/* Red 28 × 3 bar at 45° across the P. */}
        <span class="absolute h-[3px] w-7 rotate-45 rounded-full bg-[#e53935]" />
      </Show>
    </span>
  );
}
